import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Phone, ChevronUp, ChevronDown, CheckCircle2, MessageSquare, ShieldCheck, Headphones, CalendarCheck, Banknote, Workflow, MoreVertical, Trash2 } from "lucide-react";
import { cn } from "@util/tailwind";
import type { CampaignListItem, CampaignStatus } from "@type/agent";
import { USE_CASE_OPTIONS } from "@type/agent";
import { getAgentCampaigns, getAgentCallSessions, updateCampaignStatus, deleteAgentCampaign } from "@service/agent";
import { Button } from "@component/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@component/dropdown-menu";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@component/alert-dialog";
import { formatMYT } from "@util/date";
import { isSessionEffectivelyTerminal } from "@util/agent-scoring";

const useCaseIcons: Record<string, { icon: React.ComponentType<{ className?: string }> }> = {
    lead_qualification: { icon: CheckCircle2 },
    survey: { icon: MessageSquare },
    customer_verification: { icon: ShieldCheck },
    support_automation: { icon: Headphones },
    appointment_scheduling: { icon: CalendarCheck },
    collections: { icon: Banknote },
    custom: { icon: Workflow },
};

const statusConfig: Record<CampaignStatus, { label: string; dotColor: string; badgeClass: string }> = {
    active: { label: "Active", dotColor: "bg-green-500", badgeClass: "bg-green-50 text-green-700 border-green-200" },
    paused: { label: "Paused", dotColor: "bg-yellow-500", badgeClass: "bg-yellow-50 text-yellow-700 border-yellow-200" },
    archived: { label: "Archived", dotColor: "bg-gray-400", badgeClass: "bg-gray-100 text-gray-600 border-gray-200" },
    draft: { label: "Draft", dotColor: "bg-blue-400", badgeClass: "bg-blue-50 text-blue-700 border-blue-200" },
    completed: { label: "Completed", dotColor: "bg-emerald-500", badgeClass: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

type FilterStatus = "all" | CampaignStatus;

const statusFilters: { value: FilterStatus; label: string }[] = [
    { value: "all", label: "All" },
    { value: "active", label: "Active" },
    { value: "completed", label: "Completed" },
    { value: "paused", label: "Paused" },
    { value: "draft", label: "Draft" },
    { value: "archived", label: "Archived" },
];

export default function AgentCallSetupContainer() {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
    const [allCampaigns, setAllCampaigns] = useState<CampaignListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");
    const [sortField, setSortField] = useState<string>("updatedAt");
    const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
    const [deleteTarget, setDeleteTarget] = useState<CampaignListItem | null>(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Fetch all campaigns once on mount for tab counts, then auto-complete eligible ones
    useEffect(() => {
        getAgentCampaigns()
            .then((all) => {
                setAllCampaigns(all);
                // Auto-complete active campaigns that are 100% done
                const eligibleCampaigns = all.filter(
                    (c) => c.status === "active" && c.contactCount > 0 && c.sessionCount >= c.contactCount
                );
                if (eligibleCampaigns.length === 0) return;
                // Verify each: check that all real sessions are terminal before completing
                eligibleCampaigns.forEach(async (campaign) => {
                    try {
                        const res = await getAgentCallSessions({ campaignId: campaign.id, limit: 1000 });
                        const realSessions = res.sessions.filter((s) => !s.isTestCall);
                        // Skip test-call-only campaigns
                        if (realSessions.length === 0) return;
                        const allTerminal = realSessions.every(isSessionEffectivelyTerminal);
                        const uniquePhones = new Set(realSessions.map((s) => s.contactPhone).filter(Boolean));
                        if (allTerminal && uniquePhones.size >= campaign.contactCount) {
                            await updateCampaignStatus(campaign.id, "completed");
                            setAllCampaigns((prev) =>
                                prev.map((c) => c.id === campaign.id ? { ...c, status: "completed" as CampaignStatus } : c)
                            );
                            setCampaigns((prev) =>
                                prev.map((c) => c.id === campaign.id ? { ...c, status: "completed" as CampaignStatus } : c)
                            );
                        }
                    } catch {
                        // Silent — auto-complete is best-effort
                    }
                });
            })
            .catch(() => {});
    }, []);

    // Fetch filtered campaigns when filter changes
    useEffect(() => {
        const fetchCampaigns = async () => {
            setLoading(true);
            try {
                const data = await getAgentCampaigns(filterStatus === "all" ? undefined : filterStatus);
                setCampaigns(data);
            } catch {
                setCampaigns([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCampaigns();
    }, [filterStatus]);

    const statusCounts = useMemo(() => ({
        all: allCampaigns.length,
        active: allCampaigns.filter(c => c.status === "active").length,
        completed: allCampaigns.filter(c => c.status === "completed").length,
        paused: allCampaigns.filter(c => c.status === "paused").length,
        draft: allCampaigns.filter(c => c.status === "draft").length,
        archived: allCampaigns.filter(c => c.status === "archived").length,
    }), [allCampaigns]);

    const sortedCampaigns = useMemo(() => {
        return [...campaigns].sort((a, b) => {
            let cmp = 0;
            switch (sortField) {
                case "name": cmp = a.name.localeCompare(b.name); break;
                case "sessionCount": cmp = a.sessionCount - b.sessionCount; break;
                case "contactCount": cmp = a.contactCount - b.contactCount; break;
                case "updatedAt": cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(); break;
            }
            return sortDirection === "asc" ? cmp : -cmp;
        });
    }, [campaigns, sortField, sortDirection]);

    const handleSort = (field: string) => {
        if (sortField === field) {
            setSortDirection(prev => prev === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortDirection("desc");
        }
    };

    const handleDeleteCampaign = async () => {
        if (!deleteTarget) return;
        setDeleteLoading(true);
        try {
            await deleteAgentCampaign(deleteTarget.id);
            setAllCampaigns((prev) => prev.filter((c) => c.id !== deleteTarget.id));
            setCampaigns((prev) => prev.filter((c) => c.id !== deleteTarget.id));
        } catch {
            // silently fail — could add toast later
        } finally {
            setDeleteLoading(false);
            setDeleteTarget(null);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Campaigns</h1>
                    <p className="text-sm text-gray-500 mt-1">Configure and manage your call campaigns.</p>
                </div>
                <Button onClick={() => navigate("/agent/call-setup/new")}>
                    <Plus className="w-4 h-4 mr-1" />
                    New Campaign
                </Button>
            </div>

            {/* Filter tabs with counts */}
            <div className="flex gap-1.5">
                {statusFilters.map((f) => {
                    const count = statusCounts[f.value] || 0;
                    return (
                        <button
                            key={f.value}
                            onClick={() => setFilterStatus(f.value)}
                            className={cn(
                                "inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors",
                                filterStatus === f.value
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "text-gray-500 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                            )}
                        >
                            {f.label}
                            <span className={cn(
                                "text-xs font-medium",
                                filterStatus === f.value ? "opacity-70" : "opacity-50"
                            )}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-20 text-gray-400">
                    <p className="text-sm">Loading campaigns...</p>
                </div>
            ) : campaigns.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <Phone className="w-10 h-10 mb-3" />
                    <p className="text-sm font-medium">No campaigns found.</p>
                    <p className="text-xs mt-1">Create a new campaign to get started.</p>
                </div>
            ) : (
                <>
                    {/* Table */}
                    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
                        {/* Header row */}
                        <div
                            className="grid bg-[#fafbfd] dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5"
                            style={{ gridTemplateColumns: "minmax(180px, 300px) 0.8fr 1fr 0.8fr 0.7fr 0.6fr 0.7fr 0.7fr 1fr 40px" }}
                        >
                            <SortHeader
                                label="Campaign"
                                field="name"
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center">
                                Campaign ID
                            </div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center">
                                Use Case
                            </div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center">
                                Status
                            </div>
                            <SortHeader
                                label="Calls"
                                field="sessionCount"
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                align="center"
                            />
                            <SortHeader
                                label="Contacts"
                                field="contactCount"
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                                align="center"
                            />
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center justify-center">
                                % Completed
                            </div>
                            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center justify-center">
                                % Success
                            </div>
                            <SortHeader
                                label="Updated"
                                field="updatedAt"
                                sortField={sortField}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <div />
                        </div>

                        {/* Data rows */}
                        {sortedCampaigns.map((campaign) => {
                            const config = statusConfig[campaign.status];
                            const UseCaseIcon = useCaseIcons[campaign.useCase]?.icon || Workflow;
                            const completedPct = campaign.contactCount > 0
                                ? Math.min(100, Math.round((campaign.sessionCount / campaign.contactCount) * 100))
                                : 0;

                            return (
                                <div
                                    key={campaign.id}
                                    className="grid border-b border-gray-100 dark:border-gray-800 last:border-b-0 hover:bg-[#f8f9fd] dark:hover:bg-gray-800/70 cursor-pointer transition-[background] duration-100 px-4 py-2.5 items-center"
                                    style={{ gridTemplateColumns: "minmax(180px, 300px) 0.8fr 1fr 0.8fr 0.7fr 0.6fr 0.7fr 0.7fr 1fr 40px" }}
                                    onClick={() => navigate(`/agent/call-setup/${campaign.id}`)}
                                >
                                    {/* Campaign name + icon */}
                                    <div className="flex items-center gap-3 min-w-0">
                                        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                            <UseCaseIcon className="w-4 h-4" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                            {campaign.name}
                                        </span>
                                    </div>

                                    {/* Campaign ID */}
                                    <div className="text-sm text-gray-500 dark:text-gray-400 font-mono truncate">
                                        {campaign.id.slice(0, 8)}
                                    </div>

                                    {/* Use Case */}
                                    <div className="text-xs text-gray-500 truncate">
                                        {USE_CASE_OPTIONS[campaign.useCase]?.label || campaign.useCase}
                                    </div>

                                    {/* Status badge */}
                                    <div>
                                        <span className={cn(
                                            "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium border",
                                            config.badgeClass
                                        )}>
                                            <div className={cn("w-1.5 h-1.5 rounded-full", config.dotColor)} />
                                            {config.label}
                                        </span>
                                    </div>

                                    {/* Calls */}
                                    <div className={cn(
                                        "text-sm text-center",
                                        campaign.sessionCount === 0 ? "text-gray-300 dark:text-gray-600" : "text-gray-700 dark:text-gray-300"
                                    )}>
                                        {campaign.sessionCount}
                                    </div>

                                    {/* Contacts */}
                                    <div className={cn(
                                        "text-sm text-center",
                                        campaign.contactCount === 0 ? "text-gray-300 dark:text-gray-600" : "text-gray-700 dark:text-gray-300"
                                    )}>
                                        {campaign.contactCount}
                                    </div>

                                    {/* % Completed */}
                                    <div className={cn(
                                        "text-sm text-center",
                                        completedPct === 0 ? "text-gray-300 dark:text-gray-600" : "text-gray-700 dark:text-gray-300"
                                    )}>
                                        {completedPct}%
                                    </div>

                                    {/* % Success */}
                                    <div className={cn(
                                        "text-sm text-center",
                                        campaign.successRate == null ? "text-gray-300 dark:text-gray-600" : "text-gray-700 dark:text-gray-300"
                                    )}>
                                        {campaign.successRate != null ? `${Math.round(campaign.successRate)}%` : "—"}
                                    </div>

                                    {/* Updated */}
                                    <div className="text-xs text-gray-400">
                                        {formatMYT(campaign.updatedAt, "D MMM YYYY")}
                                    </div>

                                    {/* Actions */}
                                    <div onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <button className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600">
                                                    <MoreVertical className="w-4 h-4" />
                                                </button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem
                                                    className="text-red-600 focus:text-red-600"
                                                    onClick={() => setDeleteTarget(campaign)}
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-3 px-1">
                        <span className="text-xs text-gray-400 font-medium">
                            {campaigns.length} campaign{campaigns.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                </>
            )}

            <AlertDialog open={!!deleteTarget} onOpenChange={() => !deleteLoading && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete <strong>{deleteTarget?.name}</strong> and all its call history, recordings, and contact data. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={deleteLoading}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={deleteLoading}
                            className="bg-red-600 hover:bg-red-700"
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteCampaign();
                            }}
                        >
                            {deleteLoading ? "Deleting..." : "Delete"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/* ─── Sort Header Component ─────────────────────────────────────── */

function SortHeader({
    label,
    field,
    sortField,
    sortDirection,
    onSort,
    align = "left",
}: {
    label: string;
    field: string;
    sortField: string;
    sortDirection: "asc" | "desc";
    onSort: (field: string) => void;
    align?: "left" | "center";
}) {
    return (
        <button
            onClick={() => onSort(field)}
            className={cn(
                "flex items-center gap-1 text-xs font-semibold text-gray-400 uppercase tracking-wider hover:text-gray-600 dark:hover:text-gray-300 transition-colors",
                align === "center" && "justify-center"
            )}
        >
            {label}
            {sortField === field ? (
                sortDirection === "asc" ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />
            ) : (
                <ChevronDown className="w-3.5 h-3.5 opacity-30" />
            )}
        </button>
    );
}
